





































































































































/* General */
import Vue from 'vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import ColorAssistant from '@/helpers/color-assistant'
import RouteAssistant from '@/helpers/route-assistant'

/* Data objects */
import { IValidationResult } from '@/types/data-objects/global'
import { EEntities } from '@/types'
import { IAttachment } from '@/types/data-objects/attachments'
import { ITasksInputCreate } from '@/api/types/request.types'
import {
  ITaskForKanban,
  IAtMentionUser,
  IDescriptionTemplate,
  IJob,
  IJobAssigneeMandate,
  IPersonWithEmail,
  IPostSuccess,
  IPriorityLevelForSelection,
  IStatusForSelection,
  ITasksOutputCreateWithFreshnessInfo,
} from '@/api/types/response.types'
import { ICreateItemStorage, IJobInfo } from '@/types/data-objects/items'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import FilesAPI from '@/api/modules/files.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

enum CreateButtonTypes {
  NotSelected = 0,
  CreateAndClose,
  CreateAndClear,
  CreateAndKeep,
}

export default Vue.extend({
  name: 'AddTaskForm',

  props: {
    selectedProjectId: {
      type: Number,
      required: true,
    },
    originatingConversationId: {
      type: Number,
      default: null,
    },
    originatingTaskId: {
      type: Number,
      default: null,
    },
    /* Required if forKanban or forListing. Will call "CreateWithFreshnessInfo" on submit. */
    lastAccessEpochTime: {
      type: Number,
      default: null,
    },
    forKanban: {
      type: Boolean,
      default: false,
    },
    forListing: {
      type: Boolean,
      default: false,
    },
    customStickyTopPixels: {
      type: Number,
      default: null,
    },
    defaultDescription: {
      type: String,
      default: null,
    },
    livesInDrawer: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      EEntities,
      ColorAssistant,
      browser: this.$deviceInfo.getBrowser,
      projectName: '',

      // UI controls
      isSubmitting: false,
      pressedCreateButton: CreateButtonTypes.NotSelected,

      // Focus controls
      focusTitle: true,

      // Form
      formData: {
        title: '',
        description: '',
        assigneeID: null,
        statusID: null,
        priorityID: null,
        dueDate: null,
        listTags: [],
        listWatchersIDs: [],
        listAtMentionIDs: [],
        listFileUploadIDs: [],
        listLinkedTaskIDs: [],
        listJobAppointeeMappings: [],
        originatingConversationID: null,
      } as ITasksInputCreate,

      isLoading: false,

      // Options
      attachments: [] as IAttachment[],
      statusOptions: [] as IStatusForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneesOptions: [] as IPersonWithEmail[],
      tagsOptions: [] as string[],
      watchersOptions: [] as IPersonWithEmail[],
      mentionOptions: [] as IAtMentionUser[],
      descriptionTemplates: [] as IDescriptionTemplate[],

      jobs: [] as IJob[],
      assigneeMandates: [] as IJobAssigneeMandate[],
      clearAttachments: false,
    }
  },

  computed: {
    pickerSize(): string {
      return this.$screen.isTabletOrMobile(this.$mq) ? 'medium' : 'small'
    },
    projectID(): number {
      return this.selectedProjectId
    },
    hasJobs(): boolean {
      return this.jobs.length > 0
    },
    currentAssignee(): IPersonWithEmail {
      let result = null
      if (this.hasJobs) {
        /* Manually grab the correct assignee for the emitted task */
        const assigneeMandate = this.assigneeMandates.find(
          am => am.statusID === this.formData.statusID,
        )

        if (assigneeMandate) {
          const appointeeMapping = this.formData.listJobAppointeeMappings.find(
            jam => jam.jobID === assigneeMandate.jobID,
          )
          result = this.assigneesOptions.find(
            ao => ao.id === appointeeMapping.appointeeID,
          )
        }
      } else if (this.formData.assigneeID) {
        result = this.assigneesOptions.find(
          a => a.id === this.formData.assigneeID,
        )
      }
      return result
    },
    currentStatus(): IStatusForSelection {
      if (this.formData.statusID) {
        return this.statusOptions.find(s => s.id === this.formData.statusID)
      }
      return null
    },
    jobInfo(): IJobInfo {
      if (!this.hasJobs) {
        return null
      }

      const jobAppointees = this.jobs.map(j => ({
        job: j,
        appointee: this.getPotentialAssigneeByID(
          this.formData.listJobAppointeeMappings.find(jam => jam.jobID === j.id)
            .appointeeID,
        ),
      }))

      return {
        jobAppointees,
        assigneeMandates: this.assigneeMandates,
        currentAssignee: this.currentAssignee,
        currentStatus: this.currentStatus,
      }
    },
    titleOrDescriptionModified(): boolean {
      return !(
        this.formData.title === '' &&
        (this.formData.description === '' ||
          this.formData.description === '<p><br></p>')
      )
    },
  },

  watch: {
    'formData.title': {
      handler() {
        this.handleTitleDescChange()
      },
    },
    'formData.description': {
      handler() {
        this.handleTitleDescChange()
      },
    },
    projectID: {
      async handler(value) {
        this.clearFormForProjectChange()
        this.isLoading = true
        try {
          await Promise.all([this.fetchProjectData(), this.fetchOptions()])
          this.resetWatchers()
          this.resetJobAppointeeMappings()
          this.defaultRequiredFields()
        } catch (error) {
          this.$error.emit(error, true)
        } finally {
          this.isLoading = false
        }
      },
    },
    originatingConversationId: {
      immediate: true,
      handler(value: number) {
        if (value) {
          this.formData.originatingConversationID = value
        }
      },
    },
    originatingTaskId: {
      immediate: true,
      handler(value: number) {
        if (value) {
          // Gotcha: Because this happens immediately, it will automatically
          // clear the local storage for this task's project. OK with this for now.
          this.formData.listLinkedTaskIDs = [value]
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.fetchProjectData(), this.fetchOptions()])
      this.hydrateFieldsFromLocalStorage()
      this.defaultRequiredFields()
      this.resetJobAppointeeMappings()

      if (this.defaultDescription !== null) {
        this.formData.description = this.defaultDescription
      }

      this.resetWatchers()
    } catch (error) {
      this.$error.emit(error)
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    handleTitleDescChange() {
      const storage = {
        title: this.formData.title,
        description: this.formData.description,
      } as ICreateItemStorage
      LocalStore.setTaskCreateData(this.projectID, storage)

      if (this.titleOrDescriptionModified) {
        this.$emit('title-desc-modified')
      } else {
        this.$emit('title-desc-reset')
      }
    },
    defaultRequiredFields() {
      this.formData.statusID = this.statusOptions[0].id
      this.formData.priorityID = this.priorityOptions[0].id
    },
    getPotentialAssigneeByID(id?: number): IPersonWithEmail {
      if (id) {
        return this.assigneesOptions.find(ao => ao.id === id)
      }
      return null
    },
    resetJobAppointeeMappings() {
      this.formData.listJobAppointeeMappings = []
      this.jobs.forEach(j => {
        this.formData.listJobAppointeeMappings.push({
          jobID: j.id,
          appointeeID: null,
        })
      })
    },
    async fetchProjectData() {
      const response = await InternalProjectsAPI.get_Detail(this.projectID)
      this.projectName = response.name
      /* Currently only used to hydrate task-create-topbar on task-create */
      this.$emit('project-name-updated', this.projectName)
    },
    async fetchOptions() {
      const response = await TasksAPI.get_FilterOptions(this.projectID)

      this.statusOptions = response.listStatuses
      this.priorityOptions = response.listPriorities
      this.watchersOptions = response.listWatchers
      this.assigneesOptions = response.listAssignees
      this.tagsOptions = response.listTags
      this.mentionOptions = response.listAtMentionUsers
      this.descriptionTemplates = response.listDescriptionTemplates

      this.jobs = response.listJobs
      this.assigneeMandates = response.listAssigneeMandates
    },
    clearLocalStorageData() {
      LocalStore.clearTaskCreateData(this.projectID)
    },
    resetWatchers() {
      if (this.formData.listWatchersIDs.length === 0) {
        this.formData.listWatchersIDs = [this.$permissions.loggedInUser.id]
      }
    },
    /* Only persisting for title and description */
    hydrateFieldsFromLocalStorage() {
      try {
        const savedFormData = LocalStore.getTaskCreateData(this.projectID)
        if (savedFormData) {
          this.formData.title = savedFormData.title.toString()
          // Quill will scrub sql injection
          this.formData.description = savedFormData.description.toString()
        }
      } catch (e) {
        this.clearLocalStorageData()
        this.clearFormFully()
        return
      }
    },

    addFileHandler(files: IAttachment[]) {
      this.attachments = files
      this.formData.listFileUploadIDs = files.map(
        (item: IAttachment) => item.id,
      )
    },

    async removeFileHandler(id: number) {
      try {
        await FilesAPI.delete_Delete(id)
      } catch {
        // eslint-disable
      } finally {
        this.attachments = this.attachments.filter(item => item.id !== id)
        this.formData.listFileUploadIDs = this.attachments.map(item => item.id)
      }
    },

    async create(): Promise<IPostSuccess> {
      this.clearLocalStorageData()
      this.isSubmitting = true
      try {
        return await TasksAPI.post_Create(this.projectID, this.formData)
      } catch (error) {
        throw error
      } finally {
        this.isSubmitting = false
      }
    },

    async createWithFreshnessInfo(): Promise<
      ITasksOutputCreateWithFreshnessInfo
    > {
      this.clearLocalStorageData()
      this.isSubmitting = true
      try {
        const formData = {
          title: this.formData.title,
          description: this.formData.description,
          assigneeID: this.formData.assigneeID,
          statusID: this.formData.statusID,
          priorityID: this.formData.priorityID,
          dueDate: this.formData.dueDate,
          listTags: this.formData.listTags,
          listWatchersIDs: this.formData.listWatchersIDs,
          listAtMentionIDs: this.formData.listAtMentionIDs,
          listFileUploadIDs: this.formData.listFileUploadIDs,
          listJobAppointeeMappings: this.formData.listJobAppointeeMappings,
          listLinkedTaskIDs: null,
        } as ITasksInputCreate
        return await TasksAPI.post_CreateWithFreshnessInfo(
          this.projectID,
          formData,
        )
      } catch (error) {
        throw error
      } finally {
        this.isSubmitting = false
      }
    },

    clearFormForProjectChange() {
      this.formData.assigneeID = null
      this.formData.statusID = null
      this.formData.listWatchersIDs = []
      this.formData.listJobAppointeeMappings = []
    },

    clearFormFully() {
      this.formData.title = ''
      this.formData.description = ''
      this.formData.assigneeID = null
      this.formData.priorityID = null
      this.formData.statusID = null
      this.formData.dueDate = null
      this.formData.listTags = []
      this.formData.listWatchersIDs = []
      this.formData.listAtMentionIDs = []
      this.formData.listFileUploadIDs = []
      this.formData.listJobAppointeeMappings = []

      // Set watchers to current user
      this.resetWatchers()
      this.resetJobAppointeeMappings()
      this.defaultRequiredFields()

      this.attachments = []
      this.clearAttachments = true

      // Reset focus
      this.refocusTitleField()
    },

    /**
     * send to home page
     */
    sendToHome() {
      RouteAssistant.goToProjectLanding(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
        this.$permissions,
        this.projectName,
      )
    },

    clearFocuses() {
      this.focusTitle = false
    },

    refocusTitleField() {
      this.clearFocuses()
      this.$nextTick(() => {
        this.focusTitle = true
      })
    },

    handleCancelClick() {
      this.clearLocalStorageData()
      this.$emit('canceled')
    },

    insertMentions(matches: any) {
      this.formData.listAtMentionIDs = matches
    },

    async validateAndSubmit(createButton: CreateButtonTypes) {
      this.pressedCreateButton = createButton
      const createAnother = createButton !== CreateButtonTypes.CreateAndClose
      const validationResult: IValidationResult = this.validate()

      if (!validationResult.success) {
        this.refocusTitleField()
        this.$messaging.showError(validationResult.message)
        return
      }

      try {
        if (this.forKanban || this.forListing) {
          const response = await this.createWithFreshnessInfo()
          this.$messaging.showSuccess('Task added!')

          if (this.forKanban) {
            const task = {
              taskID: response.newTaskID,
              refNumber: response.newRefNumber,
              lastUpdated: response.lastUpdated,
              lastUpdatedEpochTime: response.lastUpdatedEpochTimeAfterUpdate,
              title: this.formData.title,
              statusID: this.formData.statusID,
              priorityLevelID: this.formData.priorityID,
              assigneeID: this.formData.assigneeID,
              dueDate: this.formData.dueDate,
              filterMatch: false,
            } as ITaskForKanban

            if (this.hasJobs) {
              /* Manually grab the correct assignee for the emitted task */
              task.assigneeID = null

              const assigneeMandate = this.assigneeMandates.find(
                am => am.statusID === this.formData.statusID,
              )

              if (assigneeMandate) {
                const appointeeMapping = this.formData.listJobAppointeeMappings.find(
                  jam => jam.jobID === assigneeMandate.jobID,
                )
                task.assigneeID = appointeeMapping.appointeeID
              }
            }

            this.$emit(
              'task-created-for-kanban',
              task,
              createAnother,
              response.lastUpdatedEpochTimeAfterUpdate,
            )
          } else {
            this.$emit(
              'task-created-for-listing',
              createAnother,
              response.lastUpdatedEpochTimeAfterUpdate,
            )
          }
        } else {
          const response = await this.create()
          this.$messaging.showSuccess(response.message)
          this.$emit('task-created', response.id, createAnother)
        }

        if (createButton !== CreateButtonTypes.CreateAndKeep) {
          this.clearFormFully()
        }
        if (createAnother) {
          this.refocusTitleField()
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    validate(): IValidationResult {
      if (this.formData.title.trim().length === 0) {
        return { success: false, message: 'Please enter a title.' }
      }
      return { success: true }
    },
  },
})
